/**
 * Centralized logging utility
 * Replace all console.log statements with this logger
 *
 * Usage:
 * import { logger } from './utils/logger';
 * logger.log('User action', userData);
 * logger.error('API failed', error);
 */

import { captureException, isSentryEnabled } from './sentry';

const isDev = import.meta.env.DEV;
const isProduction = import.meta.env.PROD;

type LogLevel = 'log' | 'info' | 'warn' | 'error' | 'debug';

interface LogEntry {
  timestamp: string;
  level: LogLevel;
  message: string;
  data?: unknown[];
}

class Logger {
  private logs: LogEntry[] = [];
  private maxLogs = 100;

  private formatMessage(level: LogLevel, args: unknown[]): string {
    const timestamp = new Date().toISOString();
    return `[${timestamp}] [${level.toUpperCase()}]`;
  }

  private addLog(level: LogLevel, args: unknown[]) {
    if (this.logs.length >= this.maxLogs) {
      this.logs.shift();
    }

    this.logs.push({
      timestamp: new Date().toISOString(),
      level,
      message: String(args[0] || ''),
      data: args.slice(1),
    });
  }

  /**
   * General logging - only in development
   */
  log(...args: unknown[]) {
    if (isDev) {
      console.log(this.formatMessage('log', args), ...args);
      this.addLog('log', args);
    }
  }

  /**
   * Info logging - only in development
   */
  info(...args: unknown[]) {
    if (isDev) {
      console.info(this.formatMessage('info', args), ...args);
      this.addLog('info', args);
    }
  }

  /**
   * Warning logging - always logs
   */
  warn(...args: unknown[]) {
    console.warn(this.formatMessage('warn', args), ...args);
    this.addLog('warn', args);
  }

  /**
   * Error logging - always logs and could be sent to error tracking service
   */
  error(...args: unknown[]) {
    console.error(this.formatMessage('error', args), ...args);
    this.addLog('error', args);

    // In production, send to error tracking service
    if (isProduction) {
      this.sendToErrorTracking(args);
    }
  }

  /**
   * Debug logging - only in development
   */
  debug(...args: unknown[]) {
    if (isDev) {
      console.debug(this.formatMessage('debug', args), ...args);
      this.addLog('debug', args);
    }
  }

  /**
   * Get recent logs for debugging
   */
  getRecentLogs(count = 50): LogEntry[] {
    return this.logs.slice(-count);
  }

  /**
   * Clear logs
   */
  clearLogs() {
    this.logs = [];
  }

  /**
   * Send errors to tracking service (Sentry, etc.)
   */
  private sendToErrorTracking(args: unknown[]) {
    if (!isSentryEnabled()) return;
    const first = args[0];
    if (first instanceof Error) {
      captureException(first, { logger: { extra: args.slice(1) } });
      return;
    }
    captureException(new Error(String(first ?? 'Unknown error')), {
      logger: { extra: args.slice(1) },
    });
  }
}

export const logger = new Logger();
